import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { authApi } from '@/services/api';
import {
  authPageStyles,
  authTextStyles,
  authAlertStyles,
  authVisualStyles,
  authButtonStyles,
} from '@/styles/authPageStyles';
import { CheckademicLogo } from '@/components/CheckademicLogo';
import { SchoolLogo } from '@/components/SchoolLogo';
import { LanguageToggle } from '@/components/parent/LanguageToggle';
import { useAuthStore } from '@/stores/authStore';
import { usePageTitle } from '@/hooks/usePageTitle';
import { validatePasswordStrength } from '@/utils/passwordValidation';

interface UserInfo {
  email: string;
  firstName: string;
  lastName: string;
}

function getPasswordErrorKey(password: string): string | null {
  if (password.length < 8) return 'auth.password.tooShort';
  if (password.length > 128) return 'auth.password.tooLong';
  const hasLetter = /[a-zA-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  if (!hasLetter || !hasNumber) return 'auth.password.needsLetterAndNumber';
  return null;
}

export default function SetPassword() {
  const { t } = useTranslation('common');
  usePageTitle(t('auth.setPassword.pageTitle'));
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token')?.trim() ?? null;

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const navigate = useNavigate();
  const { tenant } = useAuthStore();

  useEffect(() => {
    // Validate invitation token
    const validateToken = async () => {
      if (!token) {
        setError(t('auth.setPassword.noToken'));
        setValidating(false);
        return;
      }

      try {
        const response = await authApi.validateInvitationToken(token);

        if (response.valid && response.user) {
          setTokenValid(true);
          setUserInfo(response.user);
        } else {
          setError(t('auth.setPassword.invalidToken'));
        }
      } catch {
        setError(t('auth.setPassword.validateFailed'));
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token, t]);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    const passwordValidation = validatePasswordStrength(password);
    if (!passwordValidation.isValid) {
      const errorKey = getPasswordErrorKey(password);
      setError(t(errorKey || 'auth.password.requirements'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('auth.password.doesNotMatch'));
      return;
    }

    if (!token) {
      setError(t('auth.setPassword.noToken'));
      return;
    }

    setLoading(true);

    try {
      await authApi.setPassword(token, password);

      // Password set successfully - redirect to login
      navigate('/login?message=password_set');
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to set password. Please try again or contact support.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const logoSection = (
    <>
      {tenant?.settings.branding.logoFileId && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
          <SchoolLogo logoFileId={tenant.settings.branding.logoFileId} schoolName={tenant.schoolName} size="md" />
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
        <CheckademicLogo
          primaryColor="var(--primary-color)"
          accentColor="var(--secondary-color)"
          textColor="var(--primary-color)"
        />
      </div>
    </>
  );

  // Validating token
  if (validating) {
    return (
      <div style={authPageStyles.pageContainer}>
        <div style={authPageStyles.cardContainerCentered}>
          {logoSection}
          <div style={authVisualStyles.loadingEmoji}>
            <Loader2 size={48} aria-hidden="true" style={{ animation: 'spin 1s linear infinite' }} />
          </div>
          <p style={{ color: 'var(--text-secondary)' }}>{t('auth.setPassword.validating')}</p>
        </div>
      </div>
    );
  }

  // Invalid or expired token
  if (!tokenValid || (error && !password)) {
    return (
      <div style={authPageStyles.pageContainer}>
        <div style={authPageStyles.cardContainerCentered}>
          <LanguageToggle />
          {logoSection}
          <h1 style={authTextStyles.titleCentered}>{t('auth.setPassword.invalidTitle')}</h1>
          <p style={authTextStyles.descriptionSmall}>
            {error || t('auth.setPassword.invalidToken')}
          </p>
          <p style={authTextStyles.description}>
            {tenant?.settings?.contactEmail ? (
              <Trans
                t={t}
                i18nKey="auth.setPassword.invalidDescription"
                components={{
                  contactLink: (
                    <a
                      href={`mailto:${tenant.settings.contactEmail}?subject=${encodeURIComponent(`Invitation Link Help - ${tenant.schoolName}`)}`}
                      style={{ color: 'var(--primary-color)' }}
                    />
                  ),
                }}
              />
            ) : (
              t('auth.setPassword.invalidDescriptionNoContact')
            )}
          </p>
          <button className="btn btn-primary" onClick={() => navigate('/login')} style={authButtonStyles.fullWidth}>
            {t('auth.setPassword.goToLogin')}
          </button>
        </div>
      </div>
    );
  }

  // Valid token - show password form
  return (
    <div style={authPageStyles.pageContainer}>
      <div style={authPageStyles.cardContainer}>
        <LanguageToggle />
        {logoSection}
        <h1 style={authTextStyles.title}>{t('auth.setPassword.title')}</h1>

        {userInfo && (
          <p style={authTextStyles.description}>
            {t('auth.setPassword.welcome', { firstName: userInfo.firstName, lastName: userInfo.lastName })}<br />
            {t('auth.setPassword.welcomeEmail', { email: userInfo.email })}
          </p>
        )}

        {error && <div style={authAlertStyles.error}>{error}</div>}

        <form onSubmit={handleSetPassword}>
          <div className="form-group">
            <label className="form-label" htmlFor="password">
              {t('auth.setPassword.newPassword')}
            </label>
            <input
              id="password"
              className="form-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              disabled={loading}
              autoFocus
            />
            <p style={authTextStyles.helperText}>{t('auth.password.requirements')}</p>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="confirmPassword">
              {t('auth.setPassword.confirmPassword')}
            </label>
            <input
              id="confirmPassword"
              className="form-input"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              disabled={loading}
            />
          </div>

          <button type="submit" className="btn btn-primary" style={authButtonStyles.fullWidth} disabled={loading}>
            {loading ? t('auth.setPassword.setting') : t('auth.setPassword.setButton')}
          </button>
        </form>
      </div>
    </div>
  );
}
